import { useState, useEffect, useContext, useRef, useMemo } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

/** Poll só como rede de segurança — badge ao vivo vem do socket */
const POLL_INTERVAL_MS = 30000;
/** Debounce ao trocar filas / showAll (não atrasa evento de socket) */
const FILTER_DEBOUNCE_MS = 400;

const emptyCounts = {
  open: 0,
  pending: 0,
  group: 0,
  closed: 0,
  flow: 0,
  paused: 0,
};

/** Coalesce: requests idênticos em voo compartilham a mesma Promise */
const inflightCounts = new Map();

const fetchTicketCountsCoalesced = (paramsKey, params) => {
  const existing = inflightCounts.get(paramsKey);
  if (existing) {
    return existing;
  }

  const promise = api
    .get("/tickets/count", {
      params,
      timeout: 20000,
    })
    .then((res) => res.data)
    .finally(() => {
      if (inflightCounts.get(paramsKey) === promise) {
        inflightCounts.delete(paramsKey);
      }
    });

  inflightCounts.set(paramsKey, promise);
  return promise;
};

const normalizeCounts = (data, canAccessGroups) => {
  const normalized = {
    open: parseInt(data.open, 10) || 0,
    pending: parseInt(data.pending, 10) || 0,
    group: parseInt(data.group, 10) || 0,
    closed: parseInt(data.closed, 10) || 0,
    flow: parseInt(data.flow, 10) || 0,
    paused: parseInt(data.paused, 10) || 0,
  };
  if (!canAccessGroups) {
    normalized.group = 0;
  }
  return normalized;
};

const useTicketCounts = ({ selectedQueueIds, showAll, isRefreshing }) => {
  const { user, socket } = useContext(AuthContext);
  const [ticketCounts, setTicketCounts] = useState(emptyCounts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Incrementa em poll/socket para forçar refetch */
  const [refreshNonce, setRefreshNonce] = useState(0);

  const filterTimerRef = useRef(null);
  const mountedRef = useRef(true);
  /** true = próximo fetch sem debounce (mount, socket, poll, isRefreshing) */
  const immediateRef = useRef(true);
  const prevRefreshingRef = useRef(isRefreshing);

  const isAdmin = user?.profile === "admin";
  const canSeeAllTickets = user?.allTicket === "enabled";
  const canSeeAllUserChats = user?.allUserChat === "enabled";
  const canAccessGroups = user?.allowGroup === true;
  const canSeeAllHistory = user?.allHistoric === "enabled";

  const effectiveShowAll =
    showAll &&
    (isAdmin || canSeeAllTickets || canSeeAllHistory || canSeeAllUserChats);

  const shouldIgnoreQueueFilter = effectiveShowAll;

  const filteredQueueIds = useMemo(() => {
    const validQueueIds = Array.isArray(selectedQueueIds)
      ? selectedQueueIds
      : [];
    if (!isAdmin && canSeeAllUserChats && !canSeeAllHistory && user?.queues) {
      const userQueueIds = user.queues.map((q) => q.id);
      return validQueueIds.filter((id) => userQueueIds.includes(id));
    }
    return validQueueIds;
  }, [
    selectedQueueIds,
    isAdmin,
    canSeeAllUserChats,
    canSeeAllHistory,
    user?.queues,
  ]);

  const queueIdsKey = useMemo(
    () => [...filteredQueueIds].map(Number).sort((a, b) => a - b).join(","),
    [filteredQueueIds]
  );

  const bumpImmediate = () => {
    immediateRef.current = true;
    setRefreshNonce((n) => n + 1);
  };

  // Poll de segurança (badge ao vivo = socket)
  useEffect(() => {
    const interval = setInterval(bumpImmediate, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Badge ao vivo: create/update/delete → refetch imediato (coalescido no front + singleflight no back)
  useEffect(() => {
    if (!socket || !user?.companyId) {
      return undefined;
    }

    const companyId = user.companyId;
    const onTicket = (data) => {
      if (
        data?.action === "create" ||
        data?.action === "update" ||
        data?.action === "delete"
      ) {
        bumpImmediate();
      }
    };

    socket.on(`company-${companyId}-ticket`, onTicket);
    return () => {
      socket.off(`company-${companyId}-ticket`, onTicket);
    };
  }, [socket, user?.companyId]);

  // isRefreshing do pai (lista) também força count imediato
  useEffect(() => {
    if (prevRefreshingRef.current !== isRefreshing) {
      prevRefreshingRef.current = isRefreshing;
      if (isRefreshing != null) {
        immediateRef.current = true;
        setRefreshNonce((n) => n + 1);
      }
    }
  }, [isRefreshing]);

  useEffect(() => {
    mountedRef.current = true;

    const runFetch = async () => {
      if (!mountedRef.current || !user?.id) return;

      setLoading(true);
      setError(null);

      const params = {
        queueIds: JSON.stringify(filteredQueueIds),
        showAll: showAll ? "true" : "false",
      };
      const paramsKey = `${user.companyId}:${user.id}:${queueIdsKey}:${params.showAll}`;

      try {
        const data = await fetchTicketCountsCoalesced(paramsKey, params);
        if (!mountedRef.current) return;

        if (data && typeof data === "object") {
          setTicketCounts(normalizeCounts(data, canAccessGroups));
        } else {
          setError(new Error("Formato de dados inválido"));
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current);
      filterTimerRef.current = null;
    }

    const delay = immediateRef.current ? 0 : FILTER_DEBOUNCE_MS;
    immediateRef.current = false;

    if (delay === 0) {
      runFetch();
    } else {
      filterTimerRef.current = setTimeout(runFetch, delay);
    }

    return () => {
      mountedRef.current = false;
      if (filterTimerRef.current) {
        clearTimeout(filterTimerRef.current);
        filterTimerRef.current = null;
      }
    };
  }, [
    queueIdsKey,
    filteredQueueIds,
    showAll,
    refreshNonce,
    canAccessGroups,
    user?.id,
    user?.companyId,
  ]);

  return {
    ticketCounts,
    loading,
    error,
    permissions: {
      isAdmin,
      canSeeAllTickets,
      canSeeAllUserChats,
      canAccessGroups,
      canSeeAllHistory,
      effectiveShowAll,
      shouldIgnoreQueueFilter,
    },
  };
};

export default useTicketCounts;
